import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getApiError, listCourses, updateCourseStatus } from "../../services/courseApi";
import "./course.css";

function money(payment) {
  if (!payment) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: payment.currency || "INR",
    maximumFractionDigits: 0,
  }).format(payment.amount || 0);
}

const initialFilters = { search: "", status: "", type: "", provider_type: "" };

export default function CourseList() {
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const query = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(appliedFilters).filter(([, value]) => value !== "")
      ),
    [appliedFilters]
  );

  async function loadCourses() {
    setLoading(true);
    try {
      const result = await listCourses(query);
      setCourses(Array.isArray(result) ? result : []);
    } catch (error) {
      toast.error(getApiError(error, "Could not load courses"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCourses();
    // query is derived from the submitted filter state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  function submitFilters(event) {
    event.preventDefault();
    setAppliedFilters(filters);
  }

  async function toggleStatus(course) {
    const nextStatus = course.status === "PUBLISHED" ? "ARCHIVED" : "PUBLISHED";
    const verb = nextStatus === "PUBLISHED" ? "publish" : "archive";
    if (!window.confirm(`Are you sure you want to ${verb} “${course.title}”?`)) return;

    setUpdatingId(course.course_id);
    try {
      await updateCourseStatus(course.course_id, nextStatus);
      toast.success(`Course ${nextStatus === "PUBLISHED" ? "published" : "archived"}`);
      await loadCourses();
    } catch (error) {
      toast.error(getApiError(error, `Could not ${verb} course`));
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <main className="course-page">
      <div className="course-page-header">
        <div>
          <p className="course-eyebrow">Course management</p>
          <h1>Courses</h1>
          <p className="text-muted mb-0">Create, publish and manage course curricula.</p>
        </div>
        <Link className="btn btn-primary" to="/courses/new">
          <i className="bi bi-plus-lg me-2" />Create course
        </Link>
      </div>

      <form className="course-filter-card" onSubmit={submitFilters}>
        <div className="course-filter-grid">
          <label>
            <span>Search</span>
            <input
              className="form-control"
              placeholder="Course title or description"
              value={filters.search}
              onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            />
          </label>
          <label>
            <span>Status</span>
            <select
              className="form-select"
              value={filters.status}
              onChange={(event) => setFilters({ ...filters, status: event.target.value })}
            >
              <option value="">All statuses</option>
              <option value="PUBLISHED">Published</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </label>
          <label>
            <span>Course type</span>
            <select
              className="form-select"
              value={filters.type}
              onChange={(event) => setFilters({ ...filters, type: event.target.value })}
            >
              <option value="">All types</option>
              <option value="GRADED">Graded</option>
              <option value="ONE_OFF">One-off</option>
            </select>
          </label>
          <label>
            <span>Provider</span>
            <select
              className="form-select"
              value={filters.provider_type}
              onChange={(event) =>
                setFilters({ ...filters, provider_type: event.target.value })
              }
            >
              <option value="">All providers</option>
              <option value="ORGANIZATION">Organization</option>
              <option value="GURU">Guru</option>
            </select>
          </label>
        </div>
        <div className="d-flex gap-2 justify-content-end mt-3">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              setFilters(initialFilters);
              setAppliedFilters(initialFilters);
            }}
          >
            Reset
          </button>
          <button className="btn btn-primary" type="submit">Apply filters</button>
        </div>
      </form>

      <section className="course-table-card" aria-busy={loading}>
        {loading ? (
          <div className="course-state"><div className="spinner-border text-primary" /></div>
        ) : courses.length === 0 ? (
          <div className="course-state">
            <i className="bi bi-journal-x fs-1 text-muted" />
            <h2>No courses found</h2>
            <p className="text-muted">Change the filters or create the first course.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table course-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Provider</th>
                  <th>Type</th>
                  <th>Price</th>
                  <th>Sessions</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course) => (
                  <tr key={course.course_id}>
                    <td>
                      <Link className="course-title-link" to={`/courses/${course.course_id}`}>
                        {course.title}
                      </Link>
                      <div className="small text-muted">
                        {course.music_field?.name || "No music field"} · Version {course.version}
                      </div>
                    </td>
                    <td>
                      <div>{course.provider?.name || "—"}</div>
                      <div className="small text-muted text-capitalize">
                        {String(course.provider?.type || "").toLowerCase()}
                      </div>
                    </td>
                    <td>{course.type === "ONE_OFF" ? "One-off" : "Graded"}</td>
                    <td>{money(course.payment)}</td>
                    <td>{course.session_count}</td>
                    <td>
                      <span className={`course-status ${course.status?.toLowerCase()}`}>
                        {course.status}
                      </span>
                    </td>
                    <td className="text-end text-nowrap">
                      <Link
                        className="btn btn-sm btn-outline-primary me-2"
                        to={`/courses/${course.course_id}`}
                      >
                        View
                      </Link>
                      <Link
                        className="btn btn-sm btn-outline-secondary me-2"
                        to={`/courses/${course.course_id}/edit`}
                      >
                        Edit
                      </Link>
                      <button
                        className={`btn btn-sm ${
                          course.status === "PUBLISHED"
                            ? "btn-outline-danger"
                            : "btn-outline-success"
                        }`}
                        disabled={updatingId === course.course_id}
                        onClick={() => toggleStatus(course)}
                      >
                        {updatingId === course.course_id
                          ? "Saving…"
                          : course.status === "PUBLISHED"
                          ? "Archive"
                          : "Publish"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
